<template>
    <div
        :id="`call-activity-${element.id}`"
        class="reference-selector-container"
        :style="containerStyles"
    >
        <div v-if="isLoading" class="call-activity-loading">
            <Spinner :contained="true" />
        </div>
        <TreeSelect
            v-model="selectedKey"
            ref="selector-element"
            :options="options"
            placeholder=""
            @node-select="selectNode"
            selectionMode="single"
            variant="outlined"
            class="full-size-treeselect"
            @show="onShow"
            @hide="onHide"
            :loading="isLoading"
            :emptyMessage="isLoading ? 'Loading...' : 'No available options'"
        >
            <template #header v-if="allowCustomValue">
                <div
                    class="custom-value-section"
                    @mousedown="onCustomInputMouseDown"
                >
                    <IftaLabel class="pt-2">
                        <InputGroup>
                            <InputText
                                id="custom_value"
                                class="w-full"
                                v-model="customValue"
                                @keydown.enter="onCustomEnter"
                                @focus="onCustomInputFocus"
                                @blur="onCustomInputBlur"
                            />
                            <Button
                                icon="pi pi-check"
                                variant="text"
                                severity="success"
                                @click="onCustomApply"
                                aria-label="Apply custom value"
                            />
                        </InputGroup>
                        <label for="custom_value">
                            <i class="pi pi-pencil"></i>
                            Enter a custom value
                        </label>
                    </IftaLabel>
                </div>
                <div class="tree-title">
                    Or link to a Domain, Product or Process:
                </div>
            </template>
            <!-- Remove drodown icon -->
            <template #dropdownicon>&nbsp;</template>
        </TreeSelect>
    </div>
</template>

<script setup lang="ts">
import type Modeling from "bpmn-js/lib/features/modeling/Modeling";
import { type Element as BPMNElement } from "bpmn-js/lib/model/Types";
import { getBusinessObject, is } from "bpmn-js/lib/util/ModelUtil";
import type EventBus from "diagram-js/lib/core/EventBus";
import Button from "primevue/button";
import IftaLabel from "primevue/iftalabel";
import InputGroup from "primevue/inputgroup";
import InputText from "primevue/inputtext";
import type { TreeNode } from "primevue/treenode";
import TreeSelect from "primevue/treeselect";
import { computed, onMounted, ref, toRef, useTemplateRef, watch } from "vue";

import { useWorktreeManager } from "@/composables/worktree-manager";
import Spinner from "@/shared/Spinner.vue";
import { Domain } from "@/stores/domains-store";
import { EntityType } from "@/stores/entity-store";
import { Process } from "@/stores/processes-store";
import { Product } from "@/stores/products-store";

type SelectableEntity = (Process | Product | Domain) & { type: EntityType };

const { worktree, isFetching } = useWorktreeManager();

const selectorElement =
    useTemplateRef<InstanceType<typeof TreeSelect>>("selector-element");

const props = defineProps<{
    element: BPMNElement;
    modeling: Modeling;
    eventBus: EventBus;
    allowedEntityTypes: EntityType[];
    onClose: () => void;
    allowCustomValue: boolean;
}>();

const isLoading = computed(() => isFetching.value || !worktree.value);

const customValue = ref("");
const element = toRef(props.element);
const isCallActivity = computed(() => is(element.value, "bpmn:CallActivity"));
const selectedKey = ref<string | null>(null);
const hasShown = ref(false);
const isCustomInputFocused = ref(false);

// The outer `el` div in CustomLabelEditingProvider is sized to the screen-space
// element (accounting for canvas zoom), so filling it keeps this container in sync.
const containerStyles = { width: "100%", height: "100%" };

const options = computed(() => {
    const nodes: TreeNode[] = [];

    worktree.value?.domains.forEach((domain) => {
        nodes.push({
            key: domain.identity,
            label: domain.name,
            icon: "pi pi-cloud",
            selectable: props.allowedEntityTypes.includes(EntityType.DOMAIN),
            data: { ...domain, type: EntityType.DOMAIN },
            children: domain.products.map((product) => {
                return {
                    key: product.identity,
                    label: product.name,
                    icon: "pi pi-box",
                    selectable: props.allowedEntityTypes.includes(
                        EntityType.PRODUCT
                    ),
                    data: { ...product, type: EntityType.PRODUCT },
                    children: product.processes.map((process) => {
                        return {
                            key: process.identity,
                            label: process.name,
                            icon: "pi pi-share-alt",
                            selectable: props.allowedEntityTypes.includes(
                                EntityType.PROCESS
                            ),
                            data: { ...process, type: EntityType.PROCESS },
                        };
                    }),
                };
            }),
        });
    });

    return nodes;
});

const onShow = () => {
    hasShown.value = true;
};

const onHide = () => {
    // Don't close if the custom input is focused
    if (hasShown.value && !isCustomInputFocused.value) {
        // Use a small delay to ensure PrimeVue finished its hide/focus logic
        // before we unmount the entire app.
        setTimeout(() => {
            props.onClose();
        }, 0);
    }
};

const updateCustomValue = (value: string) => {
    if (!value || !hasShown.value) return;

    props.modeling.updateProperties(element.value, {
        name: value,
        calledElementId: undefined,
        calledElementType: undefined,
        calledElementPath: undefined,
        processId: undefined,
    });
    props.eventBus.fire("commandStack.changed");
};

const onCustomEnter = () => {
    updateCustomValue(customValue.value);
    selectorElement.value?.hide();
};

const onCustomApply = () => {
    updateCustomValue(customValue.value);
    selectorElement.value?.hide();
};

const onCustomInputMouseDown = (e: MouseEvent) => {
    // Prevent TreeSelect from detecting this as an outside click
    e.stopPropagation();
    isCustomInputFocused.value = true;
};

const onCustomInputFocus = () => {
    isCustomInputFocused.value = true;
};

const onCustomInputBlur = () => {
    isCustomInputFocused.value = false;
    updateCustomValue(customValue.value);
};

const selectNode = (node: TreeNode) => {
    if (node.key === "_custom") {
        return;
    }
    if (!node || !node.data) return;

    customValue.value = "";
    const selectedEntity = node.data as SelectableEntity;

    let domain: Domain | undefined;
    let product: Product | undefined;

    if (selectedEntity.type === EntityType.DOMAIN) {
        domain = selectedEntity as Domain;
    } else if (selectedEntity.type === EntityType.PRODUCT) {
        product = selectedEntity as Product;
        domain = worktree.value?.domains.find((d) =>
            d.products.some((p) => p.identity === product?.identity)
        );
    } else if (selectedEntity.type === EntityType.PROCESS) {
        const process = selectedEntity as Process;
        domain = worktree.value?.domains.find((d) =>
            d.products.some((p) => {
                if (
                    p.processes.some(
                        (proc) => proc.identity === process.identity
                    )
                ) {
                    product = p;
                    return true;
                }
                return false;
            })
        );
    }

    let referencePath = selectedEntity.name;
    if (selectedEntity.type === EntityType.PROCESS && domain && product) {
        referencePath = `${domain.name} > ${product.name} > ${selectedEntity.name}`;
    } else if (selectedEntity.type === EntityType.PRODUCT && domain) {
        referencePath = `${domain.name} > ${selectedEntity.name}`;
    }

    const properties: Record<string, string | undefined> = {
        name: referencePath,
    };

    if (isCallActivity.value) {
        properties.processId = selectedEntity.identity;
    } else {
        properties.calledElementId = selectedEntity.identity;
        properties.calledElementType = selectedEntity.type;
        properties.calledElementPath = referencePath;
    }

    props.modeling.updateProperties(element.value, properties);
    props.eventBus.fire("commandStack.changed");
};

// Removed: customValue watch - now updates only on blur/enter to prevent BPMN redraw while typing

// Initialize state
const bo = getBusinessObject(element.value);
customValue.value = bo.name || "";
const key = isCallActivity.value ? bo.processId : bo.calledElementId;
if (key) {
    selectedKey.value = key;
} else {
    selectedKey.value = null;
}

onMounted(() => {
    if (!isLoading.value) {
        setTimeout(() => {
            selectorElement.value?.show();
        }, 0);
        return;
    }
    // Wait for worktree data to load before opening the dropdown,
    // so the user sees the spinner on the element instead of a blank dropdown.
    const unwatch = watch(isLoading, (loading) => {
        if (!loading) {
            unwatch();
            setTimeout(() => {
                selectorElement.value?.show();
            }, 0);
        }
    });
});
</script>

<style scoped lang="scss">
.reference-selector-container {
    pointer-events: auto;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    background: transparent;
}

.call-activity-loading {
    position: absolute;
    inset: calc(2px * var(--canvas-scale, 1)) calc(2px * var(--canvas-scale, 1))
        calc(4px * var(--canvas-scale, 1));
    z-index: 1;

    :deep(.spinner) {
        min-height: 0;
        border-radius: calc(8px * var(--canvas-scale, 1));
    }

    :deep(.spinner .loading) {
        transform: translate(-50%, -50%) scale(var(--canvas-scale, 1));
    }
}

.full-size-treeselect {
    width: 100%;
    height: 100%;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    opacity: 0; // Hide the select itself so BPMN text is visible

    :deep(.p-treeselect-label) {
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    }

    :deep(.p-treeselect-label-container) {
        height: 100%;
    }

    :deep(.p-treeselect-trigger) {
        display: none;
    }
}

.custom-value-section {
    background-color: var(--surface-50);
    border-bottom: 1px solid var(--surface-border);

    label {
        display: flex;
        gap: 1em;
        align-items: center;
        z-index: 1;
    }
}
.tree-title {
    padding: 0.5em;
    font-weight: bold;
    border-bottom: 1px solid;
}
</style>
